import { softenLegacyGridColor } from '@/components/boundsAppearance'
import type { DensityStartSize, HomePreviewTheme } from '@/components/home/constants'
import { computeBoundsForMinSide, computeBoundsFromWindow } from '@/core/bounds'
import { hexNumberToString, hexStringToNumber } from '@/utils/color'
import { isVoxelMaterial } from '@/voxelTypes'
import type { Position } from '@/voxelMath'

/** How a new world starts: its bounds for a start size, and the theme used last (remembered). */

const LAST_HOME_THEME_STORAGE_KEY = 'huebo:home:last-theme'

/** Bounds for a start size: screen-density based, or a fixed shorter side (Huge). */
export function startSizeBounds(
  size: DensityStartSize,
  options: Parameters<typeof computeBoundsFromWindow>[1],
): Position {
  return size.minSide
    ? computeBoundsForMinSide(size.minSide, options)
    : computeBoundsFromWindow(size.voxelsPerCm, options)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function storedColorString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.startsWith('#') ? value : fallback
}

function storedPaletteColors(value: unknown, fallback: number[]): number[] {
  if (!Array.isArray(value)) return fallback
  const colors = value.filter((color): color is number => typeof color === 'number' && Number.isFinite(color))
  return colors.length > 0 ? colors : fallback
}

function storedPaletteItems(value: unknown, fallback: HomePreviewTheme['paletteItems']): HomePreviewTheme['paletteItems'] {
  if (!Array.isArray(value)) return fallback
  const items: HomePreviewTheme['paletteItems'] = []
  for (const item of value) {
    if (!isRecord(item) || typeof item.type !== 'string' || item.type.length === 0) continue
    const next: HomePreviewTheme['paletteItems'][number] = { type: item.type }
    if (typeof item.color === 'number' && Number.isFinite(item.color)) next.color = item.color
    if (isVoxelMaterial(item.material)) next.material = item.material
    items.push(next)
  }
  return items.length > 0 ? items : fallback
}

function normalizeStoredHomeTheme(value: unknown, fallback: HomePreviewTheme): HomePreviewTheme {
  if (!isRecord(value)) return fallback
  const baseColor = storedColorString(value.baseColor, fallback.baseColor)
  const guideColor = storedColorString(value.guideColor, fallback.guideColor)
  return {
    backgroundColor: storedColorString(value.backgroundColor, fallback.backgroundColor),
    baseColor,
    // A theme remembered from before the presets' grids were softened gets the softer line.
    guideColor: hexNumberToString(softenLegacyGridColor(hexStringToNumber(baseColor), hexStringToNumber(guideColor))),
    paletteColors: storedPaletteColors(value.paletteColors, fallback.paletteColors),
    paletteItems: storedPaletteItems(value.paletteItems, fallback.paletteItems),
    gridThickness: typeof value.gridThickness === 'number' && Number.isFinite(value.gridThickness)
      ? value.gridThickness
      : fallback.gridThickness,
  }
}

export function readLastHomeTheme(fallback: HomePreviewTheme): HomePreviewTheme {
  if (typeof localStorage === 'undefined') return fallback
  const raw = localStorage.getItem(LAST_HOME_THEME_STORAGE_KEY)
  if (!raw) return fallback
  try {
    return normalizeStoredHomeTheme(JSON.parse(raw), fallback)
  } catch {
    localStorage.removeItem(LAST_HOME_THEME_STORAGE_KEY)
    return fallback
  }
}

export function writeLastHomeTheme(theme: HomePreviewTheme): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(LAST_HOME_THEME_STORAGE_KEY, JSON.stringify(theme))
}

